<script lang="ts">
  /**
   * The Together tab: start a game with somebody, join one, and (optionally)
   * the games already going. TwinTen's `TogetherTab` layout (Interlock's
   * tab of the same name, called Versus before 1.2.0). The tab is named
   * "Together" in every game: it holds Versus and Co-op alike.
   *
   * - `startLabel` over `starts`: the ways to start (`StartTile`s). One
   *   start is a full-width row tile; two or more sit two to a row, and an
   *   odd last one spans its row as a row tile.
   * - `join`: "Join a game" (`JoinTile`), under the starts.
   * - `notice`: one line under the tiles, announced politely (why the tiles
   *   are off: "You need a connection to play together").
   * - `list`: the games already going, under its label, or its `empty`
   *   sentence. Absent: no list section at all.
   *
   * Desktop placement is fixed (from 1024px): the starts with Join under
   * them on the left; the list on the right, or nothing.
   *
   * Nothing here fetches. Test ids: `testid` on the page (default
   * `ui-together`); each start's and the join's own `testid`; `testids.starts`
   * on the grid of starts, `testids.notice` on the notice, and the list's
   * `labelTestid` and `emptyTestid` (no defaults).
   */
  import EmptyState from '../components/EmptyState.svelte';
  import JoinTile from '../components/JoinTile.svelte';
  import SectionLabel from '../components/SectionLabel.svelte';
  import StartTile from '../components/StartTile.svelte';
  import type { TogetherJoin, TogetherList, TogetherStart } from './menuTypes.js';

  interface Props {
    readonly startLabel: string;
    readonly starts: readonly TogetherStart[];
    readonly join?: TogetherJoin | undefined;
    readonly notice?: string | undefined;
    readonly list?: TogetherList | undefined;
    readonly testid?: string | undefined;
    readonly testids?: { readonly starts?: string; readonly notice?: string } | undefined;
  }
  const {
    startLabel,
    starts,
    join,
    notice,
    list,
    testid = 'ui-together',
    testids,
  }: Props = $props();

  /** A start that has its row to itself: the only one, or the odd last one. */
  function spans(index: number): boolean {
    return starts.length % 2 === 1 && index === starts.length - 1;
  }

  const uid = $props.id();
</script>

<div class="ui-together" class:ui-together--list={list !== undefined} data-testid={testid}>
  <div class="ui-together-start">
    <SectionLabel as="h2">{startLabel}</SectionLabel>
    <div
      class="ui-together-starts"
      class:ui-together-starts--paired={starts.length > 1}
      data-count={starts.length}
      data-testid={testids?.starts}
    >
      {#each starts as start, index (start.id)}
        <div class="ui-together-cell" class:ui-together-cell--span={spans(index)}>
          <StartTile
            title={start.title}
            detail={start.detail}
            icon={start.icon}
            tone={start.tone ?? 'accent'}
            layout={spans(index) ? 'row' : 'stacked'}
            disabled={start.disabled ?? false}
            onclick={start.onclick}
            testid={start.testid}
          />
        </div>
      {/each}
    </div>

    {#if join !== undefined}
      <JoinTile
        title={join.title}
        detail={join.detail}
        icon={join.icon}
        disabled={join.disabled ?? false}
        onclick={join.onclick}
        testid={join.testid}
      />
    {/if}

    {#if notice !== undefined}
      <p class="ui-together-notice" role="status" data-testid={testids?.notice}>{notice}</p>
    {/if}
  </div>

  {#if list !== undefined}
    <section class="ui-together-list" aria-labelledby="{uid}-list">
      <SectionLabel as="h2" id="{uid}-list" testid={list.labelTestid}>{list.label}</SectionLabel>
      {#if list.content !== undefined}
        <div class="ui-together-games">{@render list.content()}</div>
      {:else if list.empty !== undefined}
        <EmptyState text={list.empty} testid={list.emptyTestid} />
      {/if}
    </section>
  {/if}
</div>

<style>
  .ui-together {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    padding: var(--ui-space-2) var(--ui-gutter) var(--ui-space-5);
    font-family: var(--ui-font);
  }

  .ui-together-start,
  .ui-together-list {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }

  .ui-together-starts {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 0.6rem;
  }

  .ui-together-starts--paired {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  .ui-together-cell {
    display: flex;
    min-width: 0;
  }

  .ui-together-cell--span {
    grid-column: 1 / -1;
  }

  .ui-together-notice {
    margin: 0;
    font: var(--ui-text-label);
    font-weight: 400;
    line-height: 1.4;
    color: var(--ui-ink-2);
  }

  .ui-together-games {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }

  /* From 1024px, fixed: the starts and Join on the left; the list on the
     right, or nothing there. */
  @media (min-width: 1024px) {
    .ui-together {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
      column-gap: var(--ui-space-6);
      align-items: start;
      padding: var(--ui-space-5) var(--ui-gutter-wide) var(--ui-space-6);
    }

    .ui-together-start {
      grid-column: 1;
    }

    .ui-together-list {
      grid-column: 2;
      grid-row: 1;
    }
  }
</style>
